// src/features/manual/hooks/useManual.ts

import { useCallback, useEffect, useMemo, useState } from 'react';
import { manualApi } from '../api';
import type { ManualChapter, ManualIndex, ManualLang, ManualScreenLink, ManualSearchResponse } from '../types';
import { findScreenLink } from '../utils/labels';
import { debounce, isSearchable, MANUAL_SEARCH_DEBOUNCE_MS } from '../utils/search';

interface AsyncState<T> {
  data: T | null;
  isLoading: boolean;
  error: boolean;
  /** HTTP status of the failed read, when there was one (404 = no such chapter in this edition). */
  status: number | null;
  refetch: () => void;
}

/** One read per change of `key`; `load` is skipped (and the state cleared) while `enabled` is false. */
const useLoad = <T,>(load: () => Promise<T>, key: string, enabled = true): AsyncState<T> => {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState(false);
  const [status, setStatus] = useState<number | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setData(null);
      setIsLoading(false);
      setError(false);
      setStatus(null);
      return undefined;
    }
    let active = true;
    setIsLoading(true);
    setError(false);
    setStatus(null);
    load()
      .then((result) => {
        if (active) setData(result);
      })
      .catch((reason: { status?: number } | undefined) => {
        if (active) {
          setData(null);
          setError(true);
          setStatus(typeof reason?.status === 'number' ? reason.status : null);
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
    // `load` is a closure over `key`; the key is what decides when to read again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, attempt]);

  const refetch = useCallback(() => setAttempt((n) => n + 1), []);
  return { data, isLoading, error, status, refetch };
};

export const useManualIndex = (lang: ManualLang): AsyncState<ManualIndex> =>
  useLoad(() => manualApi.index(lang), `index:${lang}`);

export const useManualChapter = (id: string | undefined, lang: ManualLang): AsyncState<ManualChapter> =>
  useLoad(() => manualApi.chapter(id as string, lang), `chapter:${id}:${lang}`, Boolean(id));

// Contextual help: one read of the index per language and identity for the session, shared by every
// screen's «Ver en el manual»; a failed read is not kept, so the next screen tries again. The key
// includes the identity (role, impersonated subject) because the edition depends on it.
const screenIndexes = new Map<string, Promise<ManualIndex>>();

const loadIndexForHelp = (lang: ManualLang, identity: string): Promise<ManualIndex> => {
  const key = `${identity}|${lang}`;
  let pending = screenIndexes.get(key);
  if (!pending) {
    pending = manualApi.index(lang);
    pending.catch(() => screenIndexes.delete(key));
    screenIndexes.set(key, pending);
  }
  return pending;
};

/**
 * The manual section that explains `screen`, or null while loading, when no section of the
 * caller's edition claims it, or when the index cannot be read.
 */
export const useManualScreenLink = (screen: string | undefined, lang: ManualLang, identity: string): ManualScreenLink | null => {
  const [link, setLink] = useState<ManualScreenLink | null>(null);

  useEffect(() => {
    setLink(null);
    if (!screen) return undefined;
    let active = true;
    loadIndexForHelp(lang, identity)
      .then((index) => {
        if (active) setLink(findScreenLink(index, screen));
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [screen, lang, identity]);

  return link;
};

// Images: one authenticated fetch per image, kept as an object URL for the session; a failed
// fetch is not kept.
const imageUrls = new Map<string, Promise<string>>();

const loadImageUrl = (src: string): Promise<string> => {
  let pending = imageUrls.get(src);
  if (!pending) {
    pending = manualApi.image(src).then((blob) => URL.createObjectURL(blob));
    pending.catch(() => imageUrls.delete(src));
    imageUrls.set(src, pending);
  }
  return pending;
};

export const useManualImage = (src: string): { url: string | null; failed: boolean } => {
  const [state, setState] = useState<{ url: string | null; failed: boolean }>({ url: null, failed: false });

  useEffect(() => {
    let active = true;
    setState({ url: null, failed: false });
    loadImageUrl(src)
      .then((url) => {
        if (active) setState({ url, failed: false });
      })
      .catch(() => {
        if (active) setState({ url: null, failed: true });
      });
    return () => {
      active = false;
    };
  }, [src]);

  return state;
};

/** `value`, once it has stopped changing for `delay` ms. */
export const useDebouncedValue = <T,>(value: T, delay: number): T => {
  const [settled, setSettled] = useState(value);
  const settle = useMemo(() => debounce((next: T) => setSettled(() => next), delay), [delay]);
  useEffect(() => settle(value), [value, settle]);
  useEffect(() => () => settle.cancel(), [settle]);
  return settled;
};

export interface ManualSearchState extends AsyncState<ManualSearchResponse> {
  /** The query the results belong to (trimmed, debounced). */
  query: string;
  /** The field holds a searchable query that has not been sent yet. */
  pending: boolean;
}

/**
 * The manual search, debounced: one read once the field has been still for 250 ms and holds a
 * searchable query (2–100 characters); nothing is sent otherwise.
 */
export const useManualSearch = (query: string, lang: ManualLang): ManualSearchState => {
  const trimmed = query.trim();
  const settled = useDebouncedValue(trimmed, MANUAL_SEARCH_DEBOUNCE_MS);
  const enabled = isSearchable(settled);
  const state = useLoad(() => manualApi.search(settled, lang), `${lang}|${settled}`, enabled);
  return { ...state, query: settled, pending: trimmed !== settled && isSearchable(trimmed) };
};
